import { useEffect, useState } from "react";
import type { Knowledge } from "@/api/queries";
import { useModels, useSaveKnowledge } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";

const DICTIONARY_PLACEHOLDER = `# Domain
What this database is for, in a sentence.

## Canonical metrics
- Net sales = SUM(order_items.quantity * order_items.unit_price) where orders.status = 'paid'

## Grain and join paths
- order_items is one row per line item; join to orders on order_id.

## Pitfalls
- Cancelled orders are still present; always filter on status.`;

/**
 * The per-connection semantic layer (PRD FR-2.1, FR-2.2).
 *
 * The dictionary is injected into the agent prompt as authoritative
 * administrator notes, which is what makes a source with no native comments —
 * SQLite, most notably — usable at all. Custom instructions are *appended* to
 * the platform core prompt rather than replacing it, so prompt improvements
 * ship with upgrades without anyone re-editing per-connection knowledge.
 */
export function KnowledgeEditor({
  connectionId,
  knowledge,
  readOnly = false,
}: {
  connectionId: string;
  knowledge: Knowledge;
  /**
   * An analyst reads the dictionary but cannot change it (ACC-2): it is the
   * trust boundary M2 proved will steer a model, so editing it is an Admin's.
   */
  readOnly?: boolean;
}) {
  const save = useSaveKnowledge(connectionId);
  const { data: models } = useModels();
  const [dictionary, setDictionary] = useState(knowledge.data_dictionary);
  const [instructions, setInstructions] = useState(knowledge.custom_instructions);
  const [preferred, setPreferred] = useState(knowledge.preferred_model ?? "");

  useEffect(() => {
    setDictionary(knowledge.data_dictionary);
    setInstructions(knowledge.custom_instructions);
    setPreferred(knowledge.preferred_model ?? "");
  }, [knowledge.data_dictionary, knowledge.custom_instructions, knowledge.preferred_model]);

  const dirty =
    dictionary !== knowledge.data_dictionary ||
    instructions !== knowledge.custom_instructions ||
    preferred !== (knowledge.preferred_model ?? "");
  const coverage =
    knowledge.total_relations > 0
      ? Math.round((knowledge.commented_relations / knowledge.total_relations) * 100)
      : 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-container border border-border bg-surface p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-h4 text-ink">Documentation coverage</span>
          {knowledge.has_native_comments ? (
            <Chip tone={coverage > 0 ? "success" : "warning"}>
              {knowledge.commented_relations} of {knowledge.total_relations} relations commented
            </Chip>
          ) : (
            <Chip tone="warning">no native comments</Chip>
          )}
        </div>
        <p className="type-support text-ink-secondary mt-2">
          {knowledge.has_native_comments
            ? "This engine’s own table and column comments are read automatically. The dictionary below adds the business meaning comments do not carry: metric definitions, grain, join rules and pitfalls."
            : "This engine stores no table or column descriptions, so everything the agent knows about the schema’s meaning comes from the dictionary below."}
        </p>
      </div>

      <Textarea
        label="Data dictionary"
        hint="Markdown. Injected into the agent prompt as authoritative administrator notes. Paste DDL, view definitions, metric definitions, join rules and pitfalls."
        mono
        rows={18}
        spellCheck={false}
        placeholder={DICTIONARY_PLACEHOLDER}
        value={dictionary}
        readOnly={readOnly}
        onChange={(event) => setDictionary(event.target.value)}
      />

      <Textarea
        label="Custom instructions"
        hint="Appended to the platform core prompt — answer style, preferred views, domain vocabulary. The core prompt itself ships with upgrades, so improvements reach this deployment without re-editing anything here."
        rows={6}
        value={instructions}
        readOnly={readOnly}
        onChange={(event) => setInstructions(event.target.value)}
      />

      <Select
        label="Preferred model"
        value={preferred}
        disabled={readOnly}
        onChange={(event) => setPreferred(event.target.value)}
        hint="Which model answers questions about this source, unless the conversation picks another (PRD FR-3.3). A wide, undocumented schema is worth a larger model than a curated view."
      >
        <option value="">Use the deployment default</option>
        {(models ?? []).map((model) => (
          <option key={model.ref} value={model.ref}>
            {model.provider_name} · {model.label}
          </option>
        ))}
      </Select>

      {readOnly ? (
        <p className="type-support text-ink-secondary">
          Editing a source&rsquo;s knowledge needs the administrator key.
        </p>
      ) : null}
      <div className={readOnly ? "hidden" : "flex flex-wrap items-center gap-3"}>
        <Button
          variant="primary"
          type="button"
          loading={save.isPending}
          disabled={!dirty}
          onClick={() =>
            save.mutate({
              data_dictionary: dictionary,
              custom_instructions: instructions,
              preferred_model: preferred,
            })
          }
        >
          Save knowledge
        </Button>
        {dirty ? (
          <span className="type-support text-ink-secondary">Unsaved changes</span>
        ) : save.isSuccess ? (
          <span className="type-support text-success">Saved</span>
        ) : null}
      </div>
    </div>
  );
}
